import React, { useCallback, useEffect, useRef, useState } from 'react';
import { track } from '@/lib/analytics/index.ts';
import type { PublicRequestableEvent } from '@/lib/rta-api.ts';
import { RequestToAttendForm } from './RequestToAttendForm.tsx';
import styles from './RequestToAttend.module.css';

/** Props for the native booking-widget Request to Attend dialog. */
export interface RequestToAttendModalProps {
  /** Public GraphQL endpoint shared by eligibility and submission. */
  endpoint: string;
  /** Numeric restaurant id used for PII-free funnel analytics. */
  restaurantId: number | null;
  /** Active server-authorized requestable inventory selection. */
  selection: PublicRequestableEvent;
}

/**
 * Render the replacement Request to Attend CTA and its native dialog.
 * Selection changes close and reset the form so a stale event/inventory pair
 * can never be submitted after the user changes the widget selection.
 *
 * @param props - Public endpoint and canonical active selection.
 * @returns A full-width CTA followed by a focus-trapping native dialog.
 */
export function RequestToAttendModal({
  endpoint,
  restaurantId,
  selection,
}: RequestToAttendModalProps): React.ReactElement {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formMounted, setFormMounted] = useState(false);

  const close = useCallback(() => dialogRef.current?.close(), []);
  const trackOpened = useCallback(() => {
    if (restaurantId === null) return;
    track('Widget RTA Opened', {
      restaurant_id: restaurantId,
      booking_type: 'event',
      event_id: selection.event_id,
      event_inventory_id: selection.event_inventory_id,
      zone: selection.inventory_name ?? undefined,
    });
  }, [restaurantId, selection.event_id, selection.event_inventory_id, selection.inventory_name]);
  const trackSubmitted = useCallback(() => {
    if (restaurantId === null) return;
    track('Widget RTA Submitted', {
      restaurant_id: restaurantId,
      booking_type: 'event',
      event_id: selection.event_id,
      event_inventory_id: selection.event_inventory_id,
      zone: selection.inventory_name ?? undefined,
    });
  }, [restaurantId, selection.event_id, selection.event_inventory_id, selection.inventory_name]);

  useEffect(() => {
    dialogRef.current?.close();
    setFormMounted(false);
  }, [selection.event_id, selection.event_inventory_id]);

  return (
    <>
      <button
        className={styles.trigger}
        type="button"
        onClick={() => {
          trackOpened();
          setFormMounted(true);
          dialogRef.current?.showModal();
        }}
      >
        Request to Attend
      </button>

      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-label="Request to Attend"
        onClose={() => setFormMounted(false)}
        onClick={(event) => {
          if (event.target === dialogRef.current) close();
        }}
      >
        {formMounted ? (
          <div className={styles.dialogPanel}>
            <button className={styles.close} type="button" aria-label="Close" onClick={close}>
              ×
            </button>
            <RequestToAttendForm
              endpoint={endpoint}
              selection={selection}
              onSuccess={trackSubmitted}
            />
          </div>
        ) : null}
      </dialog>
    </>
  );
}
